import { isAcTerminalId } from "../ac";
import { getDefinition, type DefLookup } from "../catalog";
import { dcPortTerminalGroups, isDirectDcSource } from "../component-ports";
import { buildDcCircuitTopology } from "../dc-circuit-topology";
import { effectiveTerminals } from "../effective";
import { getSimulateStateSpec } from "../simulate/states";
import type { ComponentDefinition, Issue, System } from "../types";
import {
  buildGraph, computeConnectedSolarInputs, multiSourceBfs, pathCables, v,
  type Graph, type Vertex,
} from "./graph";

// Exact reviewed catalog capabilities, not a name/ID substring or the presence
// of an "off" simulation option. BatteryProtect, ACR, BMS and remote controls
// can stop operation without establishing a service isolation procedure.
const DC_DISCONNECT_POSTS: Record<string, readonly string[]> = {
  "dist-main-switch": ["a", "b"],
  "dist-bs-mseries-6005": ["a", "b"],
  "dist-bs-eseries-9001e": ["1", "2", "c"],
  "dist-1-2-both": ["1", "2", "c"],
  "dist-bs-6011": ["1", "2", "c1", "c2"],
  "dist-bep-701-md": ["in-pos", "out-pos"],
};
const PV_DISCONNECT_POSTS: Record<string, readonly string[]> = {
  "dist-solar-dp-isolator": ["in-pos", "out-pos", "in-neg", "out-neg"],
};

// These manuals explicitly call for disconnection of all current-carrying PV
// conductors (PV-array section), rather than merely disabling MPPT charging:
// https://www.victronenergy.com/media/pg/Manual_SmartSolar_MPPT_75-10_up_to_100-20/en/installation.html
// https://www.victronenergy.com/media/pg/Manual_SmartSolar_MPPT_100-30__100-50/en/installation.html
// https://www.victronenergy.com/media/pg/Manual_SmartSolar_MPPT_150-35__150-45/en/installation.html
// RS installation §3.4 explicitly requires an external DC PV disconnect:
// https://www.victronenergy.com/media/pg/SmartSolar_MPPT_RS/en/installation.html
// Other/custom models get a verification request, never an invented universal
// code requirement. This pass verifies drawn paths, not switch voltage ratings,
// physical access, safe work procedures or full AC/DC absence of voltage.
const PV_DISCONNECTION_REQUIRED = new Set([
  "mppt-victron-7510", "mppt-victron-7515", "mppt-victron-10015",
  "mppt-victron-10020", "mppt-victron-10030", "mppt-victron-10050",
  "mppt-victron-15035", "mppt-victron-15045",
  "mppt-victron-rs-450-100", "mppt-victron-rs-450-200",
]);

/** A retained catalog ID is not evidence after the electrical identity has
 * changed. Preserve presentation edits, but require the reviewed hardware,
 * terminal roles and operating behavior before applying its capability/rule. */
function matchesReviewedDefinition(def: ComponentDefinition): boolean {
  const baseline = getDefinition(def.id);
  if (!baseline) return false;
  if (def === baseline) return true;
  const electricalIdentity = (part: ComponentDefinition) => ({
    category: part.category, manufacturer: part.manufacturer, model: part.model,
    renderVariant: part.renderVariant, isFuseBlock: part.isFuseBlock,
    fuseBusGroups: part.fuseBusGroups, dcBreaker: part.dcBreaker,
    ampDraw: part.ampDraw, ampOutput: part.ampOutput, pvMaxVoc: part.pvMaxVoc,
    compatibleVoltages: part.compatibleVoltages,
    internalDcConnections: part.internalDcConnections,
    oneWayDcConnections: part.oneWayDcConnections,
    bms: part.bms, currentShunt: part.currentShunt,
    terminals: part.terminals.map(t => ({ id: t.id, polarity: t.polarity,
      direction: t.direction, maxAmp: t.maxAmp })).sort((a, b) => a.id.localeCompare(b.id)),
    simulateStates: part.simulateStates && {
      default: part.simulateStates.default,
      options: part.simulateStates.options.map(option => ({
        id: option.id, ampMultiplier: option.ampMultiplier,
        ampOverride: option.ampOverride, role: option.role, intraLinks: option.intraLinks,
      })),
    },
  });
  return JSON.stringify(electricalIdentity(def)) === JSON.stringify(electricalIdentity(baseline));
}

function possibleDisconnect(def: ComponentDefinition): boolean {
  return def.category === "distribution" && !def.isFuseBlock &&
    def.renderVariant !== "fuse-holder" &&
    !!getSimulateStateSpec(def)?.options.some(option =>
      option.ampOverride === 0 || option.intraLinks?.length === 0);
}

/** Open only the switched power connections. Same-post cable bypasses and
 * declared permanent copper bonds remain conducting even on known switches. */
function openDisconnects(
  graph: Graph, definitions: Map<string, ComponentDefinition>,
  reviewed: Record<string, readonly string[]>, includeUnknown = false,
): Graph {
  return {
    ...graph,
    adj: new Map([...graph.adj].map(([vertex, edges]) => [vertex, edges.filter(({ via }) => {
      if (via.kind !== "intra" || via.link.kind === "permanent") return true;
      const def = definitions.get(via.link.nodeId);
      if (!def || def.category !== "distribution") return true;
      const posts = reviewed[def.id];
      if (posts && matchesReviewedDefinition(def) && posts.every(id => def.terminals.some(t => t.id === id &&
        t.polarity !== "signal" && !isAcTerminalId(t.id))) &&
        posts.includes(via.link.from) && posts.includes(via.link.to)) return false;
      return !(includeUnknown && possibleDisconnect(def));
    })])),
  };
}

/** A bounded, deterministic witness of the remaining conducting feed. */
function connection(graph: Graph, seeds: Iterable<Vertex>, targets: ReadonlySet<Vertex>): string[] | undefined {
  const { dist, prev } = multiSourceBfs(graph, [...seeds]);
  const target = [...targets].find(vertex => dist.has(vertex));
  return target == null ? undefined : pathCables(graph, target, prev);
}

/** Design-time disconnection, independent of current simulated switch state.
 * Victron Wiring Unlimited §4.7 describes battery switches as isolation; its
 * MultiPlus-II operation §3.5 also permits a documented fuse-disconnection
 * procedure. Therefore an unverified inverter emits info, not a claim that
 * every inverter installation requires a separate switch.
 * https://www.victronenergy.com/media/pg/The_Wiring_Unlimited_book/en/dc-wiring.html
 * https://www.victronenergy.com/media/pg/MultiPlus-II_230V/en/operation.html */
export function runIsolationDiagnostics(system: System, lookup: DefLookup): Issue[] {
  const definitions = new Map<string, ComponentDefinition>();
  const powerPosts = new Set<Vertex>();
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = lookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    definitions.set(node.id, def);
    for (const terminal of effectiveTerminals(def, node)) {
      if (terminal.polarity !== "signal" && !isAcTerminalId(terminal.id)) powerPosts.add(v(node.id, terminal.id));
    }
  }
  // An absent terminal or signal cable cannot establish a physical supply.
  const physical: System = { ...system, cables: system.cables.filter(c =>
    powerPosts.has(v(c.fromNodeId, c.fromTerminalId)) && powerPosts.has(v(c.toNodeId, c.toTerminalId))) };
  const graph = buildGraph(physical, lookup, { excludeSense: true });
  const pvOpen = openDisconnects(graph, definitions, PV_DISCONNECT_POSTS);
  const pvPossibleOpen = openDisconnects(graph, definitions, PV_DISCONNECT_POSTS, true);
  const dcOpen = openDisconnects(graph, definitions, DC_DISCONNECT_POSTS);
  const issues: Issue[] = [];
  const pvReported = new Set<string>();

  for (const input of computeConnectedSolarInputs(physical, lookup)) {
    const panelPosts = new Set<Vertex>();
    for (const id of input.panelNodeIds) {
      for (const terminal of definitions.get(id)?.terminals ?? []) {
        if (terminal.polarity === "positive" || terminal.polarity === "negative") panelPosts.add(v(id, terminal.id));
      }
    }
    const seeds = [...input.positiveTerminals, ...input.negativeTerminals];
    const remaining = connection(pvOpen, seeds, panelPosts);
    if (remaining == null || pvReported.has(input.controllerNodeId)) continue;
    pvReported.add(input.controllerNodeId);
    const def = definitions.get(input.controllerNodeId)!;
    const node = system.nodes.find(n => n.id === input.controllerNodeId);
    const label = node?.label ?? def.shortName ?? def.name;
    // Unknown/custom hardware capable of breaking the drawn paths still needs
    // datasheet verification; lack of reviewed catalog data is not a violation.
    const unknownCouldIsolate = connection(pvPossibleOpen, seeds, panelPosts) == null;
    const required = PV_DISCONNECTION_REQUIRED.has(def.id) && matchesReviewedDefinition(def) && !unknownCouldIsolate;
    const code = required ? "pv-isolation-missing" : "pv-isolation-unverified";
    issues.push({ code, severity: required ? "warning" : "info",
      title: { key: `diag.${code}.title` },
      message: { key: `diag.${code}.message`, vars: { label } },
      fix: { key: `diag.${code}.fix` },
      nodeIds: [input.controllerNodeId, ...input.panelNodeIds], cableIds: remaining,
    });
  }

  const topology = buildDcCircuitTopology(physical, lookup);
  const batteryPositives = new Set([...topology.batteryPorts.values()].flatMap(port => port.positive));
  const batteryNegatives = new Set([...topology.batteryPorts.values()].flatMap(port => port.negative));
  const sources = topology.sources.map(source => ({
    nodeId: source.nodeId, positive: source.positive, negative: source.negative,
  }));
  // Alternators have starter input semantics too, so dcOutputPorts deliberately
  // excludes engines. They can still feed an inverter on a shared DC rail.
  for (const [nodeId, def] of definitions) {
    if (!isDirectDcSource(def) || def.category !== "engine") continue;
    for (const terms of dcPortTerminalGroups(def, "output")) sources.push({
      nodeId,
      positive: terms.filter(t => t.polarity === "positive").map(t => v(nodeId, t.id)),
      negative: terms.filter(t => t.polarity !== "positive").map(t => v(nodeId, t.id)),
    });
  }
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = definitions.get(node.id);
    if (!def || !["charger", "load"].includes(def.category) ||
      !def.terminals.some(t => isAcTerminalId(t.id) && t.direction === "output")) continue;
    const cableIds = new Set<string>();
    let unisolated = false;
    for (const terms of dcPortTerminalGroups(def, "input")) {
      const positives = terms.filter(t => t.polarity === "positive").map(t => v(node.id, t.id));
      const negatives = terms.filter(t => t.polarity !== "positive").map(t => v(node.id, t.id));
      if (!positives.length || !negatives.length) continue;
      // Source-range expansion can be deliberately bounded. Never turn its
      // empty partial result into evidence of no battery supply. Requiring a
      // physical return keeps isolated/incomplete unrelated drafts quiet;
      // cutting every reachable battery positive still proves disconnection.
      if (topology.unsupported.length > 0 && connection(graph, negatives, batteryNegatives) != null) {
        const remaining = connection(dcOpen, positives, batteryPositives);
        if (remaining != null) {
          unisolated = true;
          for (const id of remaining) cableIds.add(id);
        }
      }
      for (const source of sources) {
        if (source.nodeId === node.id) continue;
        // Require the same source's positive and return. An unfinished drawing
        // is handled by connectivity checks, not an isolation accusation.
        if (connection(graph, positives, new Set(source.positive)) == null ||
          connection(graph, negatives, new Set(source.negative)) == null) continue;
        const remaining = connection(dcOpen, positives, new Set(source.positive));
        if (remaining == null) continue;
        unisolated = true;
        for (const id of remaining) cableIds.add(id);
      }
    }
    if (!unisolated) continue;
    const code = "inverter-dc-isolation-unverified";
    issues.push({ code, severity: "info",
      title: { key: `diag.${code}.title` },
      message: { key: `diag.${code}.message`, vars: { label: node.label ?? def.shortName ?? def.name } },
      fix: { key: `diag.${code}.fix` }, nodeIds: [node.id], cableIds: [...cableIds],
    });
  }
  return issues;
}
